import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import Icons from '../../../../../components/Common/Icons/Icons';
import { Button } from '../../../../../components/Common/UI/Button';
import { guidanceUser } from '../../../../../contstant';
import { Card, CardContent, CardFooter } from '../../../../../components/Common/UI/Card';
import { Form, FormControl, FormField, FormItem, FormMessage } from '../../../../../components/Common/UI/Form';
import { Input } from '../../../../../components/Common/UI/Input';
import DualAuthDialog from './DualAuthDialog';
import { ViewerData } from '../SingleSignIn/FormContent';

function DirectSignInGuidance() {
  const navigate = useNavigate();
  const [modalOpen, setModalOpen] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [viewer, setViewer] = useState<ViewerData | null>(null);

  const targetUser = viewer?.username || '999999';
  const isViewerMode = !!viewer;

  useEffect(() => {
    const tempViewers = JSON.parse(localStorage.getItem('tempViewers') || '[]');
    const isAuthorizedUser = localStorage.getItem('isAuthorizedUser');

    if (isAuthorizedUser === 'true' && tempViewers.length > 0) {
      setViewer(tempViewers[0]);
      localStorage.removeItem('isAuthorizedUser');
    }
  }, []);

  const formSchema = z.object({
    username: z.string().refine(value => value === targetUser, {
      message: 'Incorrect username',
    }),
    password: z
      .string()
      .min(8, {
        message: 'Password must be at least 8 characters.',
      })
      .regex(/^(?=.*[0-9])(?=.*[\D])([\D0-9]+)$/, {
        message: 'Password should include numbers and letters.',
      })
      .regex(/[^A-Za-zА-Яа-яІЇҐЄіїґє0-9]|[A-ZА-ЯІЇҐЄ]/, {
        message: 'The password must contain at least one capital letter or special character',
      })
      .regex(/[a-zа-яіїґє]/, { message: 'Must contain one lowercase letter.' })
      .regex(/^\S+$/g, { message: 'Password must not contain spaces.' }),
  });

  const handleToggleShowPass = () => {
    setShowPassword(prevState => !prevState);
  };

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      username: targetUser,
      password: '',
    },
  });

  useEffect(() => {
    if (viewer) {
      form.setValue('username', viewer.username);
    }
  }, [viewer, form]);

  const usernameWatch = form.watch('username');
  const passwordWatch = form.watch('password');

  function onSubmit(values: z.infer<typeof formSchema>) {
    // Do something with the form values.
    // ✅ This will be type-safe and validated.
    setModalOpen(true);
  }

  return (
    <>
      <DualAuthDialog modalOpen={modalOpen} setModalOpen={setModalOpen} />
      <div className="text-secondary">
        <header className="max-w-6xl mt-1 mb-5 mx-auto px-4 text-secondary">
          <Button variant="ghost" size="auto" onClick={() => navigate('/')}>
            <Icons.ArrowLeft className="rotate-180 child:opacity-100 text-lg" /> Back
          </Button>
        </header>

        <div className="flex flex-col justify-center items-center gap-4 m-auto">
          <Card className="bg-white max-w-md w-full py-12 px-[70px] flex flex-col gap-4">
            <div>
              <h3 className="text-[26px] font-bold">Log In</h3>
              <p>We’re happy to have you here again!</p>
            </div>

            <CardContent className="p-0 flex flex-col gap-4">
              <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col gap-4">
                  <FormField
                    name="username"
                    render={({ field }) => (
                      <FormItem>
                        <FormControl>
                          <Input prefix="Username" variant="guidance" sizeVariant="lg" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    name="password"
                    render={({ field }) => (
                      <FormItem>
                        <FormControl>
                          <Input
                            prefix="Password"
                            type={showPassword ? 'text' : 'password'}
                            variant="guidance"
                            sizeVariant="lg"
                            onFocus={
                              !passwordWatch
                                ? () => form.setValue('password', guidanceUser.passwordPlaceholder)
                                : undefined
                            }
                            icon={
                              <Button
                                type="button"
                                size="auto"
                                variant="ghost"
                                onClick={() => handleToggleShowPass()}
                                className="hover:text-slate-600"
                              >
                                {showPassword ? <Icons.EyeFilled /> : <Icons.EyeFilled />}
                              </Button>
                            }
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <Button type="submit" variant="dark" disabled={!usernameWatch || !passwordWatch}>
                    Log In
                  </Button>
                </form>
              </Form>
            </CardContent>
            <CardFooter className="p-0 text-center justify-center">
              <Link className="text-addition6" to="#">
                Forgot your Password?
              </Link>
            </CardFooter>

            <Button type="button" variant="dark" disabled>
              New Customer
            </Button>
          </Card>

          <Card className="bg-white max-w-md w-full py-12 px-[70px] flex flex-col gap-4">
            <h3 className="font-bold">Need Help?</h3>
            <div className="grid grid-cols-[20px,2fr] items-center gap-3 text-addition2">
              <Icons.Mail width={18} height={18} />
              <a href="mailto:support@infoimageinc.com" className="pointer-events-none">
                support@infoimageinc.com
              </a>

              <Icons.Phone width={18} height={18} />
              <a href="tel:(650) 473-6388" className="pointer-events-none">
                (650) 473-6388
              </a>
            </div>
          </Card>
        </div>
      </div>
    </>
  );
}

export default DirectSignInGuidance;
